import { ReactNode, useMemo } from 'react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { DataTablePagination } from './DataTablePagination';
import type { UseDataTableReturn } from './useDataTable';
import type { PaginatedResult } from './types';

export interface DataTableProps<TData> {
  // TValue is intentionally `any` — a column array mixes accessor columns of
  // different value types, and TanStack Table's own generic-table pattern
  // relies on `any` here rather than `unknown` (which fails variance checks
  // on heterogeneous column defs).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  result?: PaginatedResult<TData>;
  state: UseDataTableReturn;
  /** True while a request for this table is in flight (pass query.isFetching, not isLoading). */
  loading?: boolean;
  emptyMessage?: string;
  toolbar?: ReactNode;
  getRowId?: (row: TData, index: number) => string;
}

/**
 * Generic server-driven table: pagination, sorting, and search are all
 * resolved by the caller's API (via `state.params`) — this component never
 * filters, sorts, or paginates client-side.
 */
export function DataTable<TData>({
  columns,
  result,
  state,
  loading,
  emptyMessage = 'No results.',
  toolbar,
  getRowId,
}: DataTableProps<TData>) {
  const data = useMemo(() => result?.items ?? [], [result]);

  const table = useReactTable({
    data,
    columns,
    state: { pagination: state.pagination, sorting: state.sorting },
    onPaginationChange: state.onPaginationChange,
    onSortingChange: state.onSortingChange,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: false,
    pageCount: result?.totalPages ?? -1,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
  });

  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;
  const columnCount = table.getAllLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {toolbar && <div className="border-b border-slate-200 p-3">{toolbar}</div>}

      {/* No internal max-height/scroll here — the page's own scroll
          container (SchoolLayout's <main>, already overflow-auto) is what
          grows and scrolls as row count increases; a nested scroll region
          here just wastes vertical space and forces a double scrollbar. */}
      <div className="relative">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      className={cn(
                        'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500',
                        canSort &&
                          'cursor-pointer select-none hover:text-slate-700',
                      )}
                    >
                      {header.isPlaceholder ? null : (
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {canSort &&
                            (sorted === 'asc' ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : sorted === 'desc' ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                            ))}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {!hasRows && loading ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-4 py-12 text-center text-slate-400"
                >
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : !hasRows ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-4 py-12 text-center text-slate-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3 align-top text-slate-700"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Overlay, not a replacement — existing rows stay mounted/visible
            while a new page/sort/search request is in flight. */}
        {loading && hasRows && (
          <div className="pointer-events-none sticky inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-white via-white/80 to-transparent py-3">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        )}
      </div>

      <DataTablePagination state={state} result={result} />
    </div>
  );
}
